'use client';

import { useEffect, useRef } from 'react';

/**
 * Cyan reading-progress bar pinned to the viewport top — width tracks
 * the current scroll fraction within the article body. Fully gated by
 * `prefers-reduced-motion: reduce` (no fill at all → static bar).
 *
 * Listens to `scroll` on `window` with `passive: true` to avoid jank.
 */
export function BlogReadingProgress({ targetSelector = 'article' }: { targetSelector?: string }) {
  const fillRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    const fill = fillRef.current;
    if (!fill) return;
    const target = document.querySelector(targetSelector) as HTMLElement | null;
    if (!target) return;

    let raf = 0;
    const compute = () => {
      const rect = target.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      const height = target.offsetHeight;
      const viewport = window.innerHeight;
      const scrollY = window.scrollY;

      // Progress: 0 at top of article in view, 1 when bottom of article passes bottom of viewport.
      const start = top - viewport * 0.2;
      const end = top + height - viewport * 0.8;
      const range = Math.max(1, end - start);
      const raw = (scrollY - start) / range;
      const pct = Math.max(0, Math.min(1, raw));
      fill.style.width = `${pct * 100}%`;
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [targetSelector]);

  return (
    <div
      className="blog-reading-progress"
      role="progressbar"
      aria-label="Reading progress"
      aria-hidden="true"
    >
      <div ref={fillRef} className="blog-reading-progress__fill" />
    </div>
  );
}
